// src/pages/Dashboard/Admin/DetailsAdherents/utils/adherentDashboardUtils.js

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

export function safeArray(value) {
  if (Array.isArray(value)) return value;

  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.data?.data)) return value.data.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.adherents)) return value.adherents;
  if (Array.isArray(value?.results)) return value.results;

  return [];
}

export function unwrapSettings(settings) {
  if (!settings) return {};

  let output = settings;

  if (Array.isArray(output)) output = output[0] || {};
  if (output?.dataValues) output = output.dataValues;

  return output || {};
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function toAbsoluteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

export function isDriveUrl(url = "") {
  return /(drive\.google\.com|docs\.google\.com|googleusercontent\.com)/i.test(
    String(url || "")
  );
}

export function formatDate(value, long = false) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(
    "fr-FR",
    long
      ? {
          day: "2-digit",
          month: "long",
          year: "numeric",
        }
      : undefined
  );
}

export function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatCurrency(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));
}

export function computeAge(dateNaissance) {
  if (!dateNaissance) return null;

  const birth = new Date(dateNaissance);
  if (Number.isNaN(birth.getTime())) return null;

  const today = new Date();

  let age = today.getFullYear() - birth.getFullYear();
  const month = today.getMonth() - birth.getMonth();

  if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age;
}

export function prevSeason(season) {
  const [start, end] = String(season || "")
    .split("-")
    .map(Number);

  if (!start || !end) return "";

  return `${start - 1}-${end - 1}`;
}

export function nextSeason(season) {
  const [start, end] = String(season || "")
    .split("-")
    .map(Number);

  if (!start || !end) return "";

  return `${start + 1}-${end + 1}`;
}

export function getSeasonsAround(activeSeason) {
  if (!activeSeason) return [];

  return [
    prevSeason(activeSeason),
    activeSeason,
    nextSeason(activeSeason),
  ].filter(Boolean);
}

export function getGroupsFromAdherent(adherent) {
  return safeArray(adherent?.Groups);
}

export function getUsersFromAdherent(adherent) {
  return safeArray(adherent?.Users);
}

export function getFullName(adherent) {
  return [adherent?.nom, adherent?.prenom].filter(Boolean).join(" ").trim();
}

export function getSortName(adherent) {
  return `${adherent?.nom || ""} ${adherent?.prenom || ""}`.trim();
}

export function sortAdherentsByName(rows) {
  return safeArray(rows).sort((a, b) =>
    getSortName(a).localeCompare(getSortName(b), "fr", {
      sensitivity: "base",
    })
  );
}

export function mergeAdherentListMeta(detail, fromList) {
  if (!detail && !fromList) return null;
  if (!detail) return fromList;
  if (!fromList) return detail;

  return {
    ...fromList,
    ...detail,
    Groups: Array.isArray(detail.Groups)
      ? detail.Groups
      : Array.isArray(fromList.Groups)
        ? fromList.Groups
        : [],
    Users: Array.isArray(detail.Users)
      ? detail.Users
      : Array.isArray(fromList.Users)
        ? fromList.Users
        : [],
  };
}

export function getAdherentStatusClass(status) {
  switch (status) {
    case "Licencié":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";

    case "Licencié Hors Club":
      return "bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/20";

    case "Inscrit":
      return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";

    case "Cours d'essai":
    case "Cours d’essai":
      return "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20";

    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";

    case "Ancien Licencié":
      return "bg-slate-900 text-white ring-slate-900 dark:bg-white dark:text-slate-900 dark:ring-white";

    case "Non Licencié - Arrêt":
      return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";

    default:
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
  }
}

export function getPaymentStatusClass(status) {
  switch (status) {
    case "Réglé":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";

    case "Partiellement réglé":
    case "Partiellement Réglé":
    case "En cours":
    case "En Attente":
      return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";

    case "Non réglé":
    case "Non Réglé":
      return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";

    case "Problème":
      return "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-500/10 dark:text-fuchsia-300 dark:ring-fuchsia-500/20";

    case "Annulé":
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";

    case "Remboursé":
      return "bg-purple-50 text-purple-700 ring-purple-200 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-500/20";

    default:
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
  }
}

export function getBooleanStatusClass(value) {
  return value
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
    : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";
}

export function getRoleClass(roleId) {
  switch (Number(roleId)) {
    case 1:
      return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";

    case 3:
      return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";

    case 2:
    default:
      return "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20";
  }
}

export function calculateReglementTotal(reglement) {
  if (!reglement) return 0;

  return (
    Number(reglement?.montant_lic_assurance || 0) +
    Number(reglement?.montant_cotisation || 0) +
    Number(reglement?.reduction_foyer || 0) +
    Number(reglement?.pass_sport || 0) +
    Number(reglement?.passclub32 || 0) +
    Number(reglement?.reduction_saison || 0)
  );
}

export function calculateAchatsStats(achats) {
  const rows = safeArray(achats);

  const total = rows.reduce(
    (sum, item) => sum + Number(item?.prix_total || 0),
    0
  );

  const regle = rows
    .filter((item) => item?.statut_reglement === "Réglé")
    .reduce((sum, item) => sum + Number(item?.prix_total || 0), 0);

  const nonRegle = rows
    .filter((item) => item?.statut_reglement === "Non Réglé")
    .reduce((sum, item) => sum + Number(item?.prix_total || 0), 0);

  return {
    total,
    regle,
    nonRegle,
    count: rows.length,
  };
}

export function calculateAttendanceStats(attendances) {
  const rows = safeArray(attendances);
  const total = rows.length;
  const presents = rows.filter((item) => Boolean(item?.presence)).length;
  const absents = total - presents;

  return {
    total,
    presents,
    absents,
  };
}

export function calculateAdherentDashboardStats({
  adherent,
  managers,
  dossier,
  reglement,
  belts,
  competitions,
  palmares,
  achats,
  attendances,
}) {
  const groups = getGroupsFromAdherent(adherent);

  return {
    groupsCount: groups.length,
    managersCount: safeArray(managers).length,
    dossierCompleted:
      Boolean(dossier?.validate_cm) &&
      Boolean(dossier?.validate_licence) &&
      Boolean(dossier?.validate_reglement),
    reglementTotal: calculateReglementTotal(reglement),
    reglementStatus: reglement?.statut_reglement || "—",
    beltsCount: safeArray(belts).length,
    competitionsCount: safeArray(competitions).length,
    palmaresCount: safeArray(palmares).length,
    achatsStats: calculateAchatsStats(achats),
    attendanceStats: calculateAttendanceStats(attendances),
  };
}

export function stripHtml(value = "") {
  try {
    const tmp = document.createElement("div");
    tmp.innerHTML = value || "";
    return tmp.textContent || tmp.innerText || "";
  } catch {
    return value || "";
  }
}